import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ArrowRight } from "lucide-react";
import { HocpMark } from "@/components/BrandLogo";
import kriypleLogo from "@/assets/kriyple-logo-navy.png";
import api from "@/lib/api";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) {
    // If already logged in, route by role.
    if (user.role === "super_admin") return <Navigate to="/platform" replace />;
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (!ok) return;
    // Route based on role + onboarding state (ADR-WF-1 v2.1 §37.4).
    // Super Admin → Platform Shell.
    // Hotel Admin (or any hotel employee) with incomplete setup → Setup Wizard.
    try {
      const meRes = await api.get("/auth/me");
      const me = meRes.data;
      if (me.role === "super_admin") {
        nav("/platform");
        return;
      }
    } catch {
      /* fall through */
    }
    nav("/");
  };

  return (
    <div
      className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.15fr_1fr]"
      style={{ background: "var(--hocp-ghost)" }}
    >
      {/* LEFT — Brand pane (midnight, minimal, poetic) */}
      <div
        className="hidden lg:flex relative overflow-hidden on-dark"
        style={{ background: "var(--brand-midnight)" }}
      >
        {/* Ambient mint warmth */}
        <div
          className="absolute -top-40 -left-24 w-[560px] h-[560px] rounded-full opacity-25 blur-[120px]"
          style={{ background: "var(--brand-mint)" }}
        />
        <div
          className="absolute -bottom-32 -right-32 w-[620px] h-[620px] rounded-full opacity-25 blur-[140px]"
          style={{ background: "var(--brand-deep-teal)" }}
        />
        {/* Faint grid */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />

        <div className="relative z-10 p-16 flex flex-col justify-between w-full text-white">
          {/* Brand mark */}
          <div className="flex items-center gap-3.5">
            <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shadow-lg">
              <HocpMark size={44} />
            </div>
            <div className="leading-none">
              <div className="font-display font-bold tracking-tight text-[20px]">HOCP</div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-white/55 mt-1.5">
                Est. 2026
              </div>
            </div>
          </div>

          {/* Hero */}
          <div className="max-w-2xl space-y-7">
            <div
              className="inline-flex items-center gap-3 text-[10.5px] font-semibold uppercase tracking-[0.24em]"
              style={{ color: "var(--hocp-gold)" }}
            >
              <span
                className="inline-block w-6 h-px"
                style={{ background: "var(--hocp-gold)" }}
              />
              Operations Control
            </div>

            <h1
              className="font-display font-bold tracking-tight leading-[0.98]"
              style={{
                fontSize: "clamp(42px, 5.4vw, 72px)",
                color: "#FFFFFF",
              }}
            >
              Hotel Operations<br/>
              Control{" "}
              <span style={{ color: "var(--hocp-gold)" }}>Platform.</span>
            </h1>

            <p
              className="text-[18px] leading-relaxed max-w-lg font-light"
              style={{ color: "rgba(255,255,255,0.72)" }}
            >
              If it&rsquo;s not in the system,{" "}
              <span style={{ color: "#FFFFFF", fontWeight: 500 }}>
                it didn&rsquo;t happen.
              </span>
            </p>
          </div>

          {/* Footer line */}
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-white/40">
            <span>Enterprise · Audited · Governed</span>
            <span>v0.2</span>
          </div>
        </div>
      </div>

      {/* RIGHT — Minimal form */}
      <div className="flex items-center justify-center px-6 sm:px-12 py-10">
        <div className="w-full max-w-sm space-y-12 animate-fade-in-up">
          {/* Mobile brand */}
          <div className="lg:hidden flex items-center gap-3">
            <HocpMark size={44} />
            <div>
              <div className="font-display font-bold tracking-tight text-[16px] text-hocp-navy">
                HOCP
              </div>
              <div className="text-[9px] tracking-[0.22em] uppercase text-hocp-textTertiary mt-0.5">
                Est. 2026
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-hocp-textTertiary">
              Sign in
            </div>
            <h2 className="font-display text-[32px] font-bold tracking-tight text-hocp-navy leading-[1.1]">
              Welcome back.
            </h2>
            <p className="text-[14.5px] text-hocp-textSecondary leading-relaxed">
              Enter your credentials to continue to your workspace.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <FloatingField
              id="email"
              type="email"
              label="Work email"
              value={email}
              onChange={setEmail}
              autoComplete="username"
              testid="login-email-input"
            />
            <FloatingField
              id="password"
              type="password"
              label="Password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              testid="login-password-input"
            />

            {error && (
              <div
                className="text-[13px] rounded-lg px-3.5 py-2.5"
                style={{
                  background: "var(--error-bg)",
                  color: "var(--error-text)",
                  border: "1px solid rgba(217,48,37,0.15)",
                }}
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-[15px] transition-all hover:shadow-hocp-md active:scale-[0.99] disabled:opacity-60"
              style={{ background: "var(--brand-mint)", color: "var(--brand-deep-teal)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--brand-mint-strong)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--brand-mint)")}
              data-testid="login-submit-button"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <span>Continue</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          <div className="text-[12px] text-hocp-textTertiary text-center leading-relaxed">
            Protected by enterprise-grade authentication.
          </div>

          {/* Powered by — Kriyple */}
          <div className="pt-2 flex flex-col items-center gap-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-hocp-textTertiary">Powered by</span>
              <img
                src={kriypleLogo}
                alt="Kriyple"
                className="h-[15px] w-auto"
              />
            </div>
            <div className="text-[11px] tracking-[0.02em] text-hocp-textTertiary">
              Technologies
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FloatingField({ id, label, type = "text", value, onChange, autoComplete, testid }) {
  const [focused, setFocused] = useState(false);
  const active = focused || value.length > 0;
  return (
    <div className="relative">
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoComplete={autoComplete}
        required
        data-testid={testid}
        className="peer w-full bg-transparent border-0 border-b pt-6 pb-2 text-[15px] text-hocp-navy focus:outline-none transition-colors"
        style={{
          borderBottomColor: focused ? "var(--hocp-orange)" : "var(--border-default)",
          borderBottomWidth: "1.5px",
        }}
      />
      <label
        htmlFor={id}
        className="absolute left-0 transition-all pointer-events-none font-medium"
        style={{
          top: active ? "2px" : "22px",
          fontSize: active ? "10.5px" : "15px",
          letterSpacing: active ? "0.14em" : "0",
          textTransform: active ? "uppercase" : "none",
          color: active
            ? focused
              ? "var(--hocp-orange)"
              : "var(--text-tertiary)"
            : "var(--text-secondary)",
        }}
      >
        {label}
      </label>
    </div>
  );
}
